import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Paper, Box, Button, Chip, List, ListItem,
  ListItemText, ListItemIcon, CircularProgress, Divider, IconButton
} from '@mui/material';
import {
  ArrowBack, MarkEmailRead, Settings, Chat, AccountBalanceWallet,
  HourglassEmpty, Star, Campaign, NotificationsActive, Delete,
  VideoCall, AccessAlarm, WbSunny, Queue, TrendingUp, LocalOffer
} from '@mui/icons-material';
import { fetchNotifications, markAsRead, markAllAsRead } from '../../store/slices/notificationSlice';
import { CANONICAL_CATEGORIES } from '../../constants/notifications';

const categoryIcons = {
  consultation: VideoCall,
  slot_reminder: AccessAlarm,
  daily_panchanga: WbSunny,
  wallet: AccountBalanceWallet,
  billing: AccountBalanceWallet,
  queue: Queue,
  earnings: TrendingUp,
  reviews: Star,
  promotions: LocalOffer,
  system: NotificationsActive,
};

const CATEGORIES = ['all', ...CANONICAL_CATEGORIES.map(c => c.key)];

const NotificationCenter = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  const { notifications, loading, hasMore, nextCursor, unreadCount } = useSelector(state => state.notifications || {});

  useEffect(() => {
    dispatch(fetchNotifications({ limit: 20 }));
  }, [dispatch]);

  const handleLoadMore = () => {
    if (hasMore && nextCursor) {
      dispatch(fetchNotifications({ cursor: nextCursor, limit: 20, category: filter === 'all' ? undefined : filter }));
    }
  };

  const handleClick = (notif) => {
    if (!notif.is_read) dispatch(markAsRead(notif.id));
    if (notif.deep_link) navigate(notif.deep_link);
  };

  const filtered = filter === 'all'
    ? (notifications || [])
    : (notifications || []).filter(n => n.category === filter);

  const Icon = (cat) => categoryIcons[cat] || NotificationsActive;

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>Notifications</Typography>
        {unreadCount > 0 && (
          <Button startIcon={<MarkEmailRead />} onClick={() => dispatch(markAllAsRead())} size="small">
            Mark all read
          </Button>
        )}
        <IconButton onClick={() => navigate('/notifications/settings')}><Settings /></IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {CATEGORIES.map(cat => {
          const catDef = CANONICAL_CATEGORIES.find(c => c.key === cat);
          const label = cat === 'all' ? 'All' : (catDef?.label || cat.charAt(0).toUpperCase() + cat.slice(1));
          return (
            <Chip
              key={cat}
              label={label}
              variant={filter === cat ? 'filled' : 'outlined'}
              color={filter === cat ? 'primary' : 'default'}
              onClick={() => setFilter(cat)}
              size="small"
            />
          );
        })}
      </Box>

      <Paper>
        {filtered.length === 0 && !loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List>
            {filtered.map((notif, i) => {
              const CatIcon = Icon(notif.category);
              return (
                <React.Fragment key={notif.id}>
                  <ListItem
                    button
                    onClick={() => handleClick(notif)}
                    sx={{
                      bgcolor: notif.is_read ? 'transparent' : 'action.hover',
                      borderLeft: notif.is_read ? 'none' : '3px solid',
                      borderColor: 'primary.main',
                    }}
                    secondaryAction={
                      <Typography variant="caption" color="text.secondary">
                        {new Date(notif.created_at).toLocaleDateString()}
                      </Typography>
                    }
                  >
                    <ListItemIcon><CatIcon color={notif.is_read ? 'disabled' : 'primary'} /></ListItemIcon>
                    <ListItemText
                      primary={notif.title}
                      secondary={notif.body}
                      primaryTypographyProps={{ fontWeight: notif.is_read ? 400 : 600 }}
                    />
                  </ListItem>
                  {i < filtered.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}

        {loading && (
          <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        )}

        {hasMore && !loading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Button onClick={handleLoadMore}>Load more</Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default NotificationCenter;
